import { OpenInNew } from '@mui/icons-material';
import { Stack, Typography, useTheme } from '@mui/material';
import { isEmpty, startCase, truncate } from 'lodash';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

// Components
import RouterLinkNoUnderline from 'src/components/common/RouterLinkNoUnderline';
import Table, { ICustomTableProps } from 'src/components/Table';
import PopupNegativeStory from 'src/features/CompanyDetail/NegativeMedia/PopupNegativeStory';

// Utilities
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import { StyledWhiteBox } from 'src/features/CompanyDetail/config';
import { useQueryCompanyExactNegativeMedia } from 'src/services/queries';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST } from 'src/utils/constants/config';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import { formatUrlNonProtocol, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';

// Utilities
// import DisclaimerAlert from 'src/components/common/DisclaimerAlert';

const NegativeMedia = ({
  isPagination = true,
  company,
  tableProps,
}: {
  isPagination?: boolean;
  company: ICompanyDetail;
  tableProps?: Partial<ICustomTableProps<INegativeMedia>>;
}) => {
  const { id } = useParams();
  const theme = useTheme();
  const [paramsExact, setParamsExact] = useState({
    limit: LIMIT_GET_LIST,
    offset: 0,
  });
  // const [paramsSimilar, setParamsSimilar] = useState({
  //   limit: LIMIT_GET_LIST,
  //   offset: 0,
  // });

  const { isFetching: isFetchingExact, data: dataExact } = useQueryCompanyExactNegativeMedia({
    id: id,
    params: paramsExact,
  });
  // const { isFetching: isFetchingSimilar, data: dataSimilar } = useQueryCompanyNegativeMedia({
  //   id: id,
  //   params: paramsSimilar,
  // });
  const [openNegativeStory, setOpenNegativeStory] = useState<{ open: boolean; story: INegativeMedia | null }>({
    open: false,
    story: null,
  });
  const listNegativeExact = getArrayDefault(dataExact?.data);
  // const listNegativeSimilar = getArrayDefault(dataSimilar?.data);

  const handleOpenNegativeStory = (story: INegativeMedia) => {
    setOpenNegativeStory({ open: true, story: story });
  };

  const handleCloseNegativeStory = () => {
    setOpenNegativeStory({ open: false, story: null });
  };

  const configColumn: ConfigCell<INegativeMedia>[] = [
    {
      id: 'title',
      disablePadding: true,
      label: 'Title',
      cell: (row: INegativeMedia) => {
        return (
          <Typography color={'text.secondary'} variant="body2" onClick={() => handleOpenNegativeStory(row)}>
            {renderTextOrDefault(row.title)}
          </Typography>
        );
      },
    },
    {
      id: 'outlet',
      disablePadding: false,
      label: 'Outlet',
      width: 150,
      cell: (row: INegativeMedia) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(startCase(row.site))}
          </Typography>
        );
      },
    },
    {
      id: 'date',
      disablePadding: false,
      width: 200,
      label: 'Date Retrieved',
      cell: (row: INegativeMedia) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, row.extracted_at)}
          </Typography>
        );
      },
    },
    {
      id: 'url',
      disablePadding: false,
      label: 'Source Link (URL)',
      cell: (row: INegativeMedia) => {
        return (
          <RouterLinkNoUnderline to={formatUrlNonProtocol(row.url || '')} target="_blank">
            <Typography ml={1} variant="body2" color="secondary.main">
              {truncate(`${renderTextOrDefault(row.url)}`, {
                length: 50,
              })}
            </Typography>
            <OpenInNew fontSize="small" color="secondary" />
          </RouterLinkNoUnderline>
        );
      },
    },
  ];

  // if (isFetchingExact || isFetchingSimilar) {

  // if (isEmpty(listNegativeExact) && isEmpty(listNegativeSimilar)) {
  if (isEmpty(listNegativeExact)) {
    return (
      <CompanyInfoNoData
        title="Negative Media Note"
        content={<>There is no publicly available negative media about this organization.</>}
      />
    );
  }

  return (
    <React.Fragment>
      <Stack spacing={3}>
        {/* {(!isEmpty(listNegativeExact) || !isEmpty(listNegativeSimilar)) && ( */}
        {/* {!isEmpty(listNegativeExact) && (
          <DisclaimerAlert
            title="Disclaimer: Negative Media Function Limitations"
            content={
              <>
                <Typography variant="body1" align="left">
                  The Negative Media function of ADAMftd is designed to assist users in identifying potential media
                  coverage related to specific companies. However, we do not take responsibility for any decisions made
                  based on this information.
                </Typography>
                <Typography variant="body1" align="left" sx={{ mt: theme.spacing(2) }}>
                  Please note that the negative media displayed may not necessarily be associated with the company in
                  question, as the system utilizes AI to identify relevant content, which may sometimes result in errors
                  or inaccuracies. Users are encouraged to verify the information independently before taking any
                  action.
                </Typography>
              </>
            }
          />
        )} */}
        {!isEmpty(listNegativeExact) && (
          <StyledWhiteBox>
            <Stack spacing={2}>
              <Typography variant="h5" align="left">
                Negative Media
              </Typography>
              {/* <Box height={600}>
              <ReactFlowComponent initialEdges={initialEdges} initialNodes={initialNodes} />
            </Box> */}
              <Table
                isPagination={isPagination}
                maxRow={5}
                onChangePage={(e) => setParamsExact({ ...paramsExact, ...{ offset: e } })}
                onChangePageSize={(e) => setParamsExact({ ...paramsExact, ...{ offset: 0, limit: e } })}
                page={paramsExact.offset}
                pageSize={paramsExact.limit}
                loading={isFetchingExact}
                data={listNegativeExact}
                totalRecords={dataExact?.total_pages ? dataExact?.total_pages * paramsExact.limit : 0}
                configColumn={configColumn}
                dataKey="id"
                {...tableProps}
              />
            </Stack>
          </StyledWhiteBox>
        )}
        {/* {!isEmpty(listNegativeSimilar) && (
          <StyledWhiteBox>
            <Stack spacing={2}>
              <Typography variant="h5" align="left">
                Similarly Named Entities
              </Typography>
              <Typography variant="body1" align="left" color="text.secondary">
                Below are articles with keywords similar to the company name “
                <Typography component="span" variant="body1" color="secondary">
                  {renderTextOrDefault(company?.name || '')}
                </Typography>
                ”. We provide more for your reference.
              </Typography>
              <Table
                maxRow={5}
                onChangePage={(e) => setParamsSimilar({ ...paramsSimilar, ...{ offset: e } })}
                onChangePageSize={(e) => setParamsSimilar({ ...paramsSimilar, ...{ offset: 0, limit: e } })}
                page={paramsSimilar.offset}
                pageSize={paramsSimilar.limit}
                loading={isFetchingSimilar}
                data={listNegativeSimilar}
                totalRecords={dataSimilar?.total_pages ? dataSimilar?.total_pages * paramsSimilar.limit : 0}
                configColumn={configColumn}
                dataKey="id"
              />
            </Stack>
          </StyledWhiteBox>
        )} */}
      </Stack>
      <PopupNegativeStory
        open={openNegativeStory.open}
        story={openNegativeStory.story}
        handleClose={handleCloseNegativeStory}
      />
    </React.Fragment>
  );
};

export default NegativeMedia;
